/**
 * Действия пользователя: сборка транзакций и отправка через TON Connect.
 *
 * Тела собирает @78/core — тот же код, что сверен с контрактом побайтово.
 * Здесь только адреса, суммы газа и порядок шагов.
 */

import { useTonConnectUI, useTonAddress } from '@tonconnect/ui-react';
import {
  CREATE_DEAL_TON,
  DEAL_MESSAGE_TON,
  PAYMENT_GAS,
  NFT_TRANSFER_TON,
  amountDueBy,
  buildAssignPosition,
  buildBuyPosition,
  buildCancelDeal,
  buildListPosition,
  buildClaimDefault,
  buildCreateDeal,
  buildDeferral,
  buildLockNft,
  buildPayment,
  buildWithdrawOwed,
  toTxMessage,
  type DealRequestInput,
  type DealTerms,
} from '@78/core';
import { getPaidTotal } from './chain.ts';

/** Транзакция живёт пять минут: дольше пользователь всё равно не подтвердит. */
const VALID_FOR = 300;

/**
 * Форма последнего запроса в кошелёк — для отчёта о сбое.
 *
 * Модульная переменная, а не состояние: она нужна ровно в момент разбора
 * отказа и ничего не рисует. Секретов в ней нет — всё это публичная часть
 * транзакции, которая в случае успеха и так легла бы в блокчейн.
 */
let lastRequest = '';

export function lastWalletRequest(): string {
  return lastRequest;
}

export function useActions() {
  const [tonConnectUI] = useTonConnectUI();
  const myAddress = useTonAddress(false);

  /**
   * Отправка в кошелёк.
   *
   * Заодно запоминает, что именно отправлялось: кошелёк умеет отказать с
   * пустым кодом и пустым текстом, и тогда по одной ошибке не понять даже,
   * был ли запрос осмысленным. Здесь остаётся форма запроса — адреса,
   * суммы, размер тела, срок годности, — без чего разбор превращается в
   * гадание.
   */
  const send = async (messages: { address: string; amount: string; payload: string }[]) => {
    const validUntil = Math.floor(Date.now() / 1000) + VALID_FOR;
    // Какой это кошелёк — половина ответа на «почему он отказал».
    // Транзакцию мы уже проверили эмуляцией: она проходит насквозь, мастер
    // её принимает. Значит отличается не запрос, а приложение на той
    // стороне, и без его имени разбор упирается в стену.
    const w = tonConnectUI.wallet;
    const device = w === null ? 'кошелёк не подключён' : `${w.device.appName} ${w.device.appVersion} ${w.device.platform}`;
    // Чей кошелёк подключён — отдельный факт от того, чей NFT выставляют:
    // у человека их бывает несколько в одном приложении, и запрос уходит
    // тому счёту, что назвал мост, а не тому, что открыт в кошельке.
    const from = w === null ? '?' : `${w.account.address.slice(0, 6)}…${w.account.address.slice(-4)} chain:${w.account.chain}`;
    lastRequest =
      messages
        .map((m) => `${m.address.slice(0, 6)}…${m.address.slice(-4)} ${m.amount} body:${m.payload.length}b`)
        .join(' | ') + ` validUntil:+${VALID_FOR}s · от ${from} · ${device}`;
    await tonConnectUI.sendTransaction({ validUntil, messages });
  };

  return {
    myAddress,
    connected: myAddress.length > 0,

    /**
     * Платёж по сделке.
     *
     * Сумма пересчитывается ЗДЕСЬ, а не берётся из снимка индексера: он может
     * отставать, и тогда человек заплатил бы меньше нужного и остался в
     * просрочке. Условия неизменны, внесённое читается из контракта — значит
     * ошибка возможна только в сторону переплаты, а переплата безопасна.
     */
    async pay(opts: {
      dealAddress: string;
      terms: DealTerms;
      startTs: number;
      /** Заплатить строго эту сумму, минуя пересчёт. */
      exactAmount?: bigint;
      /** На сколько периодов вперёд. По умолчанию до ближайшего срока. */
      periodsAhead?: number;
      /**
       * Приглашение — только к предоплате: контракт читает его один раз, при
       * переходе сделки в активную. Дальше оно игнорируется, поэтому и
       * прикладывать его к остальным платежам незачем.
       */
      referral?: { referrer: string; signature: string };
    }) {
      let amount = opts.exactAmount;
      if (amount === undefined) {
        const paid = await getPaidTotal(opts.dealAddress);
        const now = Math.floor(Date.now() / 1000);
        const ahead = (opts.periodsAhead ?? 0) * opts.terms.period;
        amount = amountDueBy(opts.terms, opts.startTs, paid, now + ahead + 1);
        if (amount <= 0n) throw new Error('nothing_to_pay');
      }

      const body = buildPayment(
        opts.referral === undefined
          ? {}
          : {
              referral: {
                referrer: opts.referral.referrer,
                signature: Buffer.from(opts.referral.signature, 'hex'),
              },
            },
      );
      // Деньги едут значением сообщения, надбавка на обработку сверху —
      // контракт вычтет ровно её, а человек видит обе цифры заранее.
      await send([toTxMessage(opts.dealAddress, amount + PAYMENT_GAS, body)]);
      return amount;
    },

    /** Перенос платежа: та же механика, но сумма в счёт долга не идёт. */
    async defer(opts: { dealAddress: string; deferFee: bigint }) {
      await send([
        toTxMessage(opts.dealAddress, opts.deferFee + PAYMENT_GAS, buildDeferral()),
      ]);
    },

    /** Передача NFT в залог. Отправляется контракту айтема, а не сделке. */
    async lockNft(opts: { nftAddress: string; dealAddress: string }) {
      const body = buildLockNft({ dealAddress: opts.dealAddress, responseTo: myAddress });
      await send([toTxMessage(opts.nftAddress, NFT_TRANSFER_TON, body)]);
    },

    /** Создание оффера. Мастер проверит условия и развернёт сделку. */
    async createDeal(opts: {
      masterAddress: string;
      collection: string;
      nftAddress: string;
      nftIndex: bigint;
      request: DealRequestInput;
    }) {
      // nonce от времени: он различает сделки с одинаковыми условиями, иначе
      // повторный оффер получил бы тот же адрес и не развернулся.
      const nonce = BigInt(Math.floor(Date.now() / 1000));
      const body = buildCreateDeal({ ...opts, nonce });
      await send([toTxMessage(opts.masterAddress, CREATE_DEAL_TON, body)]);
      return nonce;
    },

    /**
     * Админское сообщение мастеру.
     *
     * Тело собирает @78/core — тот же код, что сверен с контрактом побайтово.
     * Право менять что-либо даёт не приложение, а подпись: мастер сверит
     * отправителя со своим админом и отвергнет чужого.
     */
    async sendToMaster(masterAddress: string, tonAmount: bigint, body: Parameters<typeof toTxMessage>[2]) {
      await send([toTxMessage(masterAddress, tonAmount, body)]);
    },

    /**
     * Выставить позицию на продажу или снять с продажи (цена ноль). §8
     *
     * Цена уходит в сам контракт, а не в нашу базу: «продаётся за N» должно
     * быть проверяемым утверждением, а не обещанием сервера.
     */
    async listPosition(dealAddress: string, price: bigint) {
      await send([toTxMessage(dealAddress, DEAL_MESSAGE_TON, buildListPosition(price))]);
    },

    /**
     * Выкупить позицию продавца. §8
     *
     * Обмен атомарен: контракт в той же транзакции пересылает деньги
     * прежнему владельцу и переписывает право на оставшиеся платежи на
     * плательщика.
     */
    async buyPosition(opts: { dealAddress: string; price: bigint }) {
      await send([
        toTxMessage(opts.dealAddress, opts.price + PAYMENT_GAS, buildBuyPosition()),
      ]);
    },

    async claimDefault(dealAddress: string) {
      await send([toTxMessage(dealAddress, DEAL_MESSAGE_TON, buildClaimDefault())]);
    },

    async cancelDeal(dealAddress: string) {
      await send([toTxMessage(dealAddress, DEAL_MESSAGE_TON, buildCancelDeal())]);
    },

    /**
     * Передача стороны продавца.
     *
     * Контракт переносит право на оставшиеся платежи, но денег за позицию не
     * держит: расчёт между сторонами — их дело. Поэтому в интерфейсе это
     * передача по адресу, а не продажа с ценой.
     */
    async assignPosition(dealAddress: string, newOwner: string) {
      await send([toTxMessage(dealAddress, DEAL_MESSAGE_TON, buildAssignPosition(newOwner))]);
    },

    async withdrawOwed(dealAddress: string) {
      await send([toTxMessage(dealAddress, DEAL_MESSAGE_TON, buildWithdrawOwed())]);
    },
  };
}
